import { useCallback, useEffect, useState } from 'react'
import { Button, Popconfirm, Space, Table, Tag, Typography, message } from 'antd'
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { crmIntegrationApi, type SkippedLead } from '../services/crmIntegrationApi'
import { useAuthStore } from '../store/authStore'
import { seesAllLeads } from '../constants/roles'

const { Text, Title } = Typography

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Ошибка запроса'
}

const reasonLabels: Record<string, string> = {
    telegram_id: 'Совпал TG ID',
    telegram: 'Совпал телеграм',
    phone: 'Совпал телефон',
    name_birth: 'Совпали имя, фамилия и дата рождения',
}

// Лиды CRM Wiseway, которых мы не стали загружать: у нас уже есть карточка
// того же человека. Раздел нужен, чтобы такое расхождение можно было найти и
// сверить руками в их системе.
export default function Duplicates() {
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const isManager = seesAllLeads(user?.role)
    const [items, setItems] = useState<SkippedLead[]>([])
    const [loading, setLoading] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const response = await crmIntegrationApi.getDuplicates()
            setItems(response.data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const removeItem = async (id: number) => {
        try {
            await crmIntegrationApi.deleteDuplicate(id)
            setItems((rows) => rows.filter((row) => row.id !== id))
            message.success('Запись убрана — лид снова доступен к загрузке')
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const columns = [
        {
            title: 'ID CRM WW',
            dataIndex: 'crm_ww_id',
            key: 'crm_ww_id',
            render: (value: string) => <Text strong copyable>{value}</Text>,
        },
        {
            title: 'Лид в CRM Wiseway',
            key: 'title',
            render: (_: unknown, row: SkippedLead) => (
                <Space direction="vertical" size={0}>
                    <Text>{row.title || '—'}</Text>
                    <Text type="secondary" copyable={Boolean(row.telegram_contact)}>
                        {row.telegram_contact || row.phone || '—'}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Источник',
            dataIndex: 'source',
            key: 'source',
            render: (value: string | null) => value || '—',
        },
        {
            title: 'Причина',
            dataIndex: 'reason',
            key: 'reason',
            render: (value: string) => <Tag color="orange">{reasonLabels[value] || value}</Tag>,
        },
        {
            title: 'Наш лид',
            key: 'lead',
            render: (_: unknown, row: SkippedLead) => (row.lead_id ? (
                <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/contacts/${row.lead_id}`)}>
                    #{row.lead_id} {row.lead_title || ''}
                </Button>
            ) : <Text type="secondary">карточка удалена</Text>),
        },
        {
            title: 'Когда',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value: string) => dayjs(value).format('DD.MM.YYYY HH:mm'),
        },
        ...(isManager ? [{
            title: '',
            key: 'actions',
            render: (_: unknown, row: SkippedLead) => (
                <Popconfirm
                    title="Убрать из дублей?"
                    description="Лид снова начнёт приходить при загрузке из CRM Wiseway."
                    okText="Убрать"
                    cancelText="Отмена"
                    onConfirm={() => removeItem(row.id)}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        }] : []),
    ]

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center" wrap>
                    <div>
                        <Title level={3} style={{ margin: 0 }}>Дубли</Title>
                        <Text type="secondary">
                            Лиды CRM Wiseway, которых мы не загрузили: у нас уже есть карточка того же человека.
                            Повторно они не запрашиваются и место в порции не занимают.
                        </Text>
                    </div>
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Обновить</Button>
                </Space>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={items}
                    loading={loading}
                    scroll={{ x: 900 }}
                    pagination={{ pageSize: 20 }}
                />
            </Space>
        </div>
    )
}
